// Category-specific scoring weights and intelligence

export interface CategoryScoring {
  trustWeight: number;
  qualityWeight: number;
  deliveryWeight: number;
  priceWeight: number;
  defectRiskLevel: "low" | "medium" | "high";
  authenticityRiskLevel: "low" | "medium" | "high";
  returnPainLevel: "low" | "medium" | "high";
  durabilityExpectation: "disposable" | "moderate" | "long_term" | "investment";
  repeatPurchasePotential: "one_time" | "occasional" | "frequent";
  trustSensitivity: "normal" | "elevated" | "critical";
  premiumCeiling: number; // max reasonable price multiplier vs median
  valueFloor: number;     // min reasonable quality score
  seasonalPattern?: string;
  refreshCycle?: string;  // "annual" | "6mo" | "quarterly" | "none"
}

const CATEGORY_SCORING: Record<string, CategoryScoring> = {
  monitors: {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.15, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "low", returnPainLevel: "high",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "normal", premiumCeiling: 2.5, valueFloor: 60,
    refreshCycle: "annual",
  },
  "office chairs": {
    trustWeight: 0.25, qualityWeight: 0.35, deliveryWeight: 0.1, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "medium", returnPainLevel: "high",
    durabilityExpectation: "investment", repeatPurchasePotential: "one_time",
    trustSensitivity: "elevated", premiumCeiling: 3.0, valueFloor: 55,
    refreshCycle: "none",
  },
  vacuums: {
    trustWeight: 0.2, qualityWeight: 0.3, deliveryWeight: 0.15, priceWeight: 0.35,
    defectRiskLevel: "medium", authenticityRiskLevel: "low", returnPainLevel: "medium",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "normal", premiumCeiling: 2.0, valueFloor: 50,
    seasonalPattern: "Black Friday, Prime Day",
  },
  headphones: {
    trustWeight: 0.2, qualityWeight: 0.3, deliveryWeight: 0.2, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "moderate", repeatPurchasePotential: "occasional",
    trustSensitivity: "elevated", premiumCeiling: 2.5, valueFloor: 55,
    refreshCycle: "annual",
  },
  "espresso machines": {
    trustWeight: 0.25, qualityWeight: 0.35, deliveryWeight: 0.1, priceWeight: 0.3,
    defectRiskLevel: "high", authenticityRiskLevel: "medium", returnPainLevel: "high",
    durabilityExpectation: "investment", repeatPurchasePotential: "one_time",
    trustSensitivity: "elevated", premiumCeiling: 4.0, valueFloor: 60,
    refreshCycle: "none",
  },
  "protein powder": {
    trustWeight: 0.3, qualityWeight: 0.25, deliveryWeight: 0.2, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "critical", premiumCeiling: 1.8, valueFloor: 45,
  },
  skincare: {
    trustWeight: 0.35, qualityWeight: 0.3, deliveryWeight: 0.1, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "critical", premiumCeiling: 3.0, valueFloor: 40,
  },
  laptops: {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.15, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "medium", returnPainLevel: "high",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "elevated", premiumCeiling: 2.5, valueFloor: 65,
    refreshCycle: "annual",
  },
  // ─── New Category Profiles ────────────────────────────────────────────
  vitamins: {
    trustWeight: 0.35, qualityWeight: 0.25, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "critical", premiumCeiling: 2.0, valueFloor: 40,
  },
  supplements: {
    trustWeight: 0.35, qualityWeight: 0.25, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "critical", premiumCeiling: 2.0, valueFloor: 40,
  },
  "dog food": {
    trustWeight: 0.3, qualityWeight: 0.3, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "medium", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "elevated", premiumCeiling: 2.0, valueFloor: 50,
  },
  "cat food": {
    trustWeight: 0.3, qualityWeight: 0.3, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "medium", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "elevated", premiumCeiling: 2.0, valueFloor: 50,
  },
  strollers: {
    trustWeight: 0.3, qualityWeight: 0.35, deliveryWeight: 0.1, priceWeight: 0.25,
    defectRiskLevel: "medium", authenticityRiskLevel: "medium", returnPainLevel: "high",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "critical", premiumCeiling: 3.0, valueFloor: 60,
  },
  diapers: {
    trustWeight: 0.2, qualityWeight: 0.3, deliveryWeight: 0.25, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "low", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "normal", premiumCeiling: 1.5, valueFloor: 40,
  },
  "power tools": {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.15, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "medium", returnPainLevel: "medium",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "elevated", premiumCeiling: 2.5, valueFloor: 55,
  },
  "running shoes": {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.2, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "medium",
    durabilityExpectation: "moderate", repeatPurchasePotential: "occasional",
    trustSensitivity: "elevated", premiumCeiling: 2.0, valueFloor: 55,
    refreshCycle: "6mo",
  },
  "fitness equipment": {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.15, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "low", returnPainLevel: "high",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "normal", premiumCeiling: 2.5, valueFloor: 55,
  },
  coffee: {
    trustWeight: 0.25, qualityWeight: 0.3, deliveryWeight: 0.2, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "medium", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "normal", premiumCeiling: 2.5, valueFloor: 45,
  },
  luggage: {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.15, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "high", returnPainLevel: "medium",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "elevated", premiumCeiling: 3.0, valueFloor: 55,
  },
  mattresses: {
    trustWeight: 0.2, qualityWeight: 0.4, deliveryWeight: 0.1, priceWeight: 0.3,
    defectRiskLevel: "medium", authenticityRiskLevel: "low", returnPainLevel: "high",
    durabilityExpectation: "investment", repeatPurchasePotential: "one_time",
    trustSensitivity: "normal", premiumCeiling: 3.0, valueFloor: 60,
    refreshCycle: "none",
  },
  // ─── Scale-Up: Deeper Category Profiles ───────────────────────────────
  "hair care": {
    trustWeight: 0.3, qualityWeight: 0.3, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "critical", premiumCeiling: 2.5, valueFloor: 40,
  },
  makeup: {
    trustWeight: 0.3, qualityWeight: 0.3, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "occasional",
    trustSensitivity: "critical", premiumCeiling: 3.0, valueFloor: 40,
  },
  fragrance: {
    trustWeight: 0.35, qualityWeight: 0.25, deliveryWeight: 0.15, priceWeight: 0.25,
    defectRiskLevel: "low", authenticityRiskLevel: "high", returnPainLevel: "medium",
    durabilityExpectation: "moderate", repeatPurchasePotential: "occasional",
    trustSensitivity: "critical", premiumCeiling: 4.0, valueFloor: 45,
  },
  "pet accessories": {
    trustWeight: 0.2, qualityWeight: 0.3, deliveryWeight: 0.2, priceWeight: 0.3,
    defectRiskLevel: "low", authenticityRiskLevel: "low", returnPainLevel: "low",
    durabilityExpectation: "moderate", repeatPurchasePotential: "occasional",
    trustSensitivity: "normal", premiumCeiling: 2.0, valueFloor: 40,
  },
  "baby gear": {
    trustWeight: 0.3, qualityWeight: 0.35, deliveryWeight: 0.1, priceWeight: 0.25,
    defectRiskLevel: "medium", authenticityRiskLevel: "medium", returnPainLevel: "high",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "critical", premiumCeiling: 3.0, valueFloor: 60,
  },
  "car seats": {
    trustWeight: 0.35, qualityWeight: 0.35, deliveryWeight: 0.1, priceWeight: 0.2,
    defectRiskLevel: "high", authenticityRiskLevel: "medium", returnPainLevel: "high",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "critical", premiumCeiling: 2.5, valueFloor: 70,
  },
  "hand tools": {
    trustWeight: 0.2, qualityWeight: 0.35, deliveryWeight: 0.15, priceWeight: 0.3,
    defectRiskLevel: "low", authenticityRiskLevel: "low", returnPainLevel: "low",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "normal", premiumCeiling: 2.0, valueFloor: 50,
  },
  "garden tools": {
    trustWeight: 0.2, qualityWeight: 0.3, deliveryWeight: 0.15, priceWeight: 0.35,
    defectRiskLevel: "low", authenticityRiskLevel: "low", returnPainLevel: "medium",
    durabilityExpectation: "long_term", repeatPurchasePotential: "one_time",
    trustSensitivity: "normal", premiumCeiling: 2.0, valueFloor: 45,
    seasonalPattern: "Spring, Fall",
  },
  "household cleaners": {
    trustWeight: 0.15, qualityWeight: 0.25, deliveryWeight: 0.25, priceWeight: 0.35,
    defectRiskLevel: "low", authenticityRiskLevel: "low", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "normal", premiumCeiling: 1.5, valueFloor: 35,
  },
  laundry: {
    trustWeight: 0.15, qualityWeight: 0.25, deliveryWeight: 0.25, priceWeight: 0.35,
    defectRiskLevel: "low", authenticityRiskLevel: "low", returnPainLevel: "low",
    durabilityExpectation: "disposable", repeatPurchasePotential: "frequent",
    trustSensitivity: "normal", premiumCeiling: 1.5, valueFloor: 35,
  },
};

const DEFAULT_SCORING: CategoryScoring = {
  trustWeight: 0.25, qualityWeight: 0.25, deliveryWeight: 0.25, priceWeight: 0.25,
  defectRiskLevel: "medium", authenticityRiskLevel: "medium", returnPainLevel: "medium",
  durabilityExpectation: "moderate", repeatPurchasePotential: "occasional",
  trustSensitivity: "normal", premiumCeiling: 2.0, valueFloor: 50,
};

export function getCategoryScoring(category: string): CategoryScoring {
  const normalized = category.toLowerCase().replace(/-/g, " ").trim();
  return CATEGORY_SCORING[normalized] || DEFAULT_SCORING;
}

export function shouldRecommendWait(category: string, priceScore: number, qualityScore: number): { wait: boolean; reason?: string } {
  const scoring = getCategoryScoring(category);
  
  if (scoring.refreshCycle === "annual" && qualityScore < scoring.valueFloor) {
    return { wait: true, reason: "Current options are below quality threshold. New models may be releasing soon." };
  }
  
  if (scoring.seasonalPattern && priceScore < 60) {
    return { wait: true, reason: `Prices may drop during ${scoring.seasonalPattern}. Consider tracking.` };
  }
  
  return { wait: false };
}

export function shouldRecommendRefurbished(category: string, budget: number, medianPrice: number): boolean {
  const scoring = getCategoryScoring(category);
  if (scoring.durabilityExpectation === "disposable") return false;
  if (scoring.defectRiskLevel === "high") return false;
  return budget < medianPrice * 0.7;
}

export function getDiminishingReturnsThreshold(category: string, medianPrice: number): number {
  const scoring = getCategoryScoring(category);
  return medianPrice * scoring.premiumCeiling;
}
